import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import { Waypoint } from '@omniafishing/core';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryString } from '../../../../hooks/use_query_string';
import { useResponsive } from '../../../../hooks/use_responsive';
import { useUser } from '../../../../hooks/use_user';
import { useWaypoints } from '../../../../hooks/use_waypoints';
import { OmniaUrls } from '../../../../lib/urls';
import { useAppsflyerSmartScriptURL } from '../../../../lib/use_appsflyer_smart_script_url';
import { SignupLoginModalActions } from '../../../../redux/signup_login_modal';
import { ExtendedMapQueryParams } from '../../../../routes/map_page/lib/map_page_types';
import { CurrentConditions } from '../../../current_conditions/current_conditions';
import { CurrentConditionsTimestamp } from '../../../current_conditions/current_conditions_timestamp';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { WaypointMarker } from '../../waypoint_marker';
import { ConditionsUpgrade } from './conditions_upgrade';
import styles from './waypoint_details_screen.less';
import { truncateTo6Decimal } from './waypoint_form_helper';

interface SharedWaypointModalProps {
  onClose: () => void;
  waypoint: Waypoint;
}

export const SharedWaypointModal = (props: SharedWaypointModalProps) => {
  const { onClose, waypoint } = props;
  const appsflyerSmartScriptURL = useAppsflyerSmartScriptURL();
  const { isMobile } = useResponsive();
  const { user, isPro } = useUser();
  const dispatch = useDispatch();
  const { createWaypoint } = useWaypoints();

  const { replaceQueryString } = useQueryString();

  useEffect(() => {
    if (user && isMobile) {
      replaceQueryString<ExtendedMapQueryParams>({
        user_email: user.email,
      });
    }
  }, [user, isMobile]);

  const saveWaypoint = () => {
    createWaypoint.mutate(
      {
        comments,
        lat,
        lng,
        name,
        waterbody: waterbody?.id,
        waypoint_type,
      },
      {
        onSuccess: (data, params) => {
          onClose();
        },
      }
    );
  };

  const [showMore, setShowMore] = useState(false);

  if (!waypoint) {
    return null;
  }
  const { name, outing_at, waterbody, lat, lng, comments, waypoint_type, conditions_saved } =
    waypoint;

  return (
    <>
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div className={styles.buttonAndHeader}>
              <div>
                <span className={styles.heading}>Shared Waypoint</span>
                <span className={styles.subHeading}>
                  PUBLIC (anyone with the link can see this)
                </span>
              </div>
            </div>
            <span role="button" tabIndex={1} aria-label="close modal">
              <CloseOutlined onClick={onClose} />
            </span>
          </div>
          <div
            className={classNames(styles.sharedWaypointDetails, {
              [styles.sharedWaypointDetails__open]: showMore,
            })}
          >
            {isMobile ? (
              <div className={styles.topLineWrapper}>
                <div className={styles.sharedWaypoint__topLine}>
                  <WaypointMarker waypoint_type={waypoint_type} />
                  <div className={styles.nameDateMobile}>
                    <strong>{name}</strong>
                    <span>
                      {dayjs(outing_at).format('MMM D, YYYY')} at{' '}
                      {dayjs(outing_at).format('h:mm A')}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.sharedWaypoint__topLine}>
                  <WaypointMarker waypoint_type={waypoint_type} />
                  <strong>{name}</strong>
                </div>
                <div className={styles.date}>
                  <strong>Date/Time</strong>
                  <span>
                    {dayjs(outing_at).format('MMM D, YYYY')} at {dayjs(outing_at).format('h:mm A')}
                  </span>
                </div>
              </>
            )}
            <div className={styles.location}>
              {waterbody && (
                <div>
                  <strong>Waterbody</strong>
                  <span>{waterbody.primary_name}</span>{' '}
                </div>
              )}
              <div>
                <strong>Latitude</strong>
                <span>{truncateTo6Decimal(lat)}°</span>
              </div>
              <div>
                <strong>Longitude</strong>
                <span>{truncateTo6Decimal(lng)}°</span>
              </div>
            </div>
            <div className={styles.notes}>
              <strong>Notes</strong>
              <span>{comments}</span>
            </div>
            <section className={styles.conditions}>
              <h3 className={styles.conditionsHeading}>
                Conditions at {dayjs(outing_at).format('MMM D, YYYY')},{' '}
                {dayjs(outing_at).format('h:mm A')}
              </h3>
              {conditions_saved ? (
                <CurrentConditions
                  waypoint={waypoint}
                  isProBlocked
                  solunarLoading={false}
                  weatherLoading={false}
                />
              ) : (
                <CurrentConditionsTimestamp
                  lat={lat}
                  lng={lng}
                  timestamp={outing_at}
                  isProBlocked
                />
              )}
              {!isPro && <ConditionsUpgrade />}
            </section>
          </div>
          <div className={styles.footer}>
            <div className={styles.footerContent__shared}>
              <>
                {isMobile ? (
                  <>
                    <div className={styles.mobileShareButtonWrapper}>
                      <OmniaButton
                        kind="tertiary"
                        size="md"
                        shape="circle"
                        className={classNames(styles.moreButton, {
                          [styles.moreButton__open]: showMore,
                        })}
                        onPress={() => setShowMore(!showMore)}
                      >
                        <DownOutlined />
                      </OmniaButton>
                      <span className={styles.mobileBlurb}>
                        Waypoints are available exclusively through the Omnia App or desktop site,
                        not on mobile browsers.
                      </span>
                      <a href={appsflyerSmartScriptURL} className={styles.appsFlyerButton}>
                        Download Omnia App To Save Waypoint
                      </a>
                    </div>
                  </>
                ) : (
                  <div className={styles.buttons}>
                    <OmniaButton kind="tertiary" size="md" onPress={onClose}>
                      Dismiss
                    </OmniaButton>
                    {user ? (
                      <OmniaButton kind="primary" size="md" onPress={saveWaypoint}>
                        Save Waypoint
                      </OmniaButton>
                    ) : (
                      <OmniaButton
                        kind="primary"
                        size="md"
                        onPress={() =>
                          dispatch(
                            SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN({
                              forwardRoute: OmniaUrls.waypoint(waypoint),
                            })
                          )
                        }
                      >
                        Log In To Save Waypoint
                      </OmniaButton>
                    )}
                  </div>
                )}
              </>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
